import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../../store/authSlice";
import API from "../../api";
import "../css/LeadDetailsModal.css";

/** Pull user fields from JWT in local/session storage */
function getUserFromToken() {
  const tok =
    window.localStorage.getItem("token") ||
    window.sessionStorage.getItem("token");
  if (!tok) return {};
  const parts = tok.split(".");
  if (parts.length < 2) return {};
  try {
    const json = JSON.parse(
      atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"))
    );
    return json?.user || json || {};
  } catch {
    return {};
  }
}

/** token first -> redux-persist fallback */
function getUsernameAndRole() {
  const u = getUserFromToken();
  const usernameFromToken = u?.username || u?.name || u?.preferred_username || "";
  const roleFromToken = (u?.role || "").toLowerCase();

  if (usernameFromToken || roleFromToken) {
    return {
      username: String(usernameFromToken || ""),
      role: String(roleFromToken || ""),
    };
  }

  const keysToTry = ["persistroot", "persist:root"];
  for (const key of keysToTry) {
    try {
      const raw = window.localStorage.getItem(key);
      if (!raw) continue;

      const root = JSON.parse(raw);
      const authObj =
        typeof root?.auth === "string" ? JSON.parse(root.auth) : root?.auth;

      const username =
        authObj?.user?.username ||
        authObj?.user?.name ||
        authObj?.username ||
        "";

      const role = (authObj?.user?.role || authObj?.role || "").toLowerCase();

      return { username: String(username || ""), role: String(role || "") };
    } catch {
      // try next
    }
  }
  return { username: "", role: "" };
}

/** basic helpers */
function toDateInput(v) {
  if (!v) return "";
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  return String(v);
}

/**
 * Convert many common user-entered formats into MySQL DATE yyyy-mm-dd.
 * Accepts:
 *  - yyyy-mm-dd (returns as-is)
 *  - mm-dd-yyyy / mm/dd/yyyy
 * If unknown format, returns original string (so you can catch server-side).
 */
function toMysqlDate(v) {
  const s = String(v || "").trim();
  if (!s) return "";

  // already mysql-friendly
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;

  // mm-dd-yyyy OR mm/dd/yyyy
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) {
    const mm = String(m[1]).padStart(2, "0");
    const dd = String(m[2]).padStart(2, "0");
    const yyyy = String(m[3]);
    return `${yyyy}-${mm}-${dd}`;
  }

  return s;
}

/**
 * Returns true if the value is blank OR matches an accepted date format.
 * Accepted: YYYY-MM-DD | MM-DD-YYYY | MM/DD/YYYY
 * Also does a real calendar check (e.g. rejects 02-30-2025).
 */
function isValidDateValue(v) {
  const s = String(v || "").trim();
  if (!s) return true; // blank → required check handles it

  // YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const d = new Date(`${s}T00:00:00`);
    return !isNaN(d.getTime());
  }

  // MM-DD-YYYY or MM/DD/YYYY
  const m = s.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (m) {
    const iso = `${m[3]}-${String(m[1]).padStart(2, "0")}-${String(m[2]).padStart(2, "0")}`;
    const d = new Date(`${iso}T00:00:00`);
    return !isNaN(d.getTime());
  }

  return false; // unrecognised format
}

function isValidSSNOrBlank(ssn) {
  const s = String(ssn || "").trim();
  if (!s) return true;
  return /^\d{9}$/.test(s);
}

// hasOwnProperty helper
function hasOwn(obj, key) {
  return !!obj && Object.prototype.hasOwnProperty.call(obj, key);
}

// Hydrate helper: if key exists and value is null/undefined => ""
function pick(data, key, fallback = "") {
  if (!hasOwn(data, key)) return fallback;
  const v = data[key];
  return v === null || typeof v === "undefined" ? "" : v;
}

// number hydrate helper: if key exists and null/undefined => 0
function pickNum(data, key, fallback = 0) {
  if (!hasOwn(data, key)) return fallback;
  const v = data[key];
  if (v === null || typeof v === "undefined" || v === "") return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function isBlank(v) {
  return !String(v ?? "").trim();
}

export default function DocumentModal({
  open,
  onClose,
  lead, // expects: { lead_id, name, bname, email, mobile/phone, agent }
  onSaved, // callback
}) {
  const leadId = useMemo(
    () => lead?.lead_id || lead?.leadId || lead?.id || null,
    [lead]
  );

  const [step, setStep] = useState(0);
  const [loadingExisting, setLoadingExisting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [leadFlags, setLeadFlags] = useState({ is_reprogram: 0, is_duplicate: 0, source_lead_id: null });
  const [bannerErr, setBannerErr] = useState("");
  const [bannerOk, setBannerOk] = useState("");

  // NEW: validation shown only on final save
  const [attemptedSave, setAttemptedSave] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({}); // { [fieldName]: message }

  // equipment keys
  const equipmentKeys = useMemo(
    () => [
      "paxa920",
      "dejavoo-qd4",
      "clover-go",
      "clover-flex",
      "clover-mini",
      "clover-mini-with-Cashdrawer",
      "clover-station",
      "clover-station-duo",
    ],
    []
  );

  const emptyEquipmentCounts = useMemo(() => {
    const obj = {};
    equipmentKeys.forEach((k) => (obj[k] = 0));
    return obj;
  }, [equipmentKeys]);

  const [copyOwnerToBusiness, setCopyOwnerToBusiness] = useState(false);
  const [ssnOptional, setSsnOptional] = useState(false);

  // shipping address source selector
  const [shippingSource, setShippingSource] = useState("manual"); // manual | business | home

  // Admin-only: createdby override
  const reduxUser = useSelector(selectUser);
  const { role: currentRole } = getUsernameAndRole();
  const isAdmin = currentRole === "admin";
  const [allUsers, setAllUsers] = useState([]);
  const [selectedCreatedBy, setSelectedCreatedBy] = useState("");

  useEffect(() => {
    if (!isAdmin || !reduxUser?.id) return;
    API.get("/auth/admin/users-redux", { params: { requesterId: reduxUser.id } })
      .then((r) => {
        const list = Array.isArray(r?.data) ? r.data : (r?.data?.users || []);
        setAllUsers(list);
      })
      .catch(() => {});
  }, [isAdmin, reduxUser?.id]);

  const buildInitialForm = useMemo(() => {
    return () => ({
      // core
      lead_id: leadId || "",
      name: lead?.name || "",
      bname: lead?.bname || "",
      agent: lead?.agent || "",

      // Step 1: user details
      email: lead?.email || "",
      phone: lead?.mobile || lead?.phone || "",
      street: "",
      city: "",
      state: "",
      postal_code: "",
      DOB: "",
      SSN: "",

      // Step 2: business details
      "Legal-Name": "",
      "DBA-Name": "",
      "Tax-id": "",
      Website: "",
      "ownership-type": "",
      "Business-Address": "",
      "Business-City": "",
      "Business-State": "",
      "Business-zip": "",
      "Business-Phone": "",
      "Business-start-date": "",
      "Avg-ticket": "",
      "High-ticket": "",
      "total-monthly-processing": "",
      "products-sold": "",

      // Step 3: pricing
      feestructure: "",
      Pricing: "",
      PricingText: "",
      "Pricing-variable": "",
"monthly-fee": 0,
"monthly-minimum": 0,

      // Step 4: banking
      "Bank-name": "",
      "Bank-acc": "",
      Routing: "",

      // Step 5: equipment + shipping
      equipement: "",
      ...emptyEquipmentCounts,
      "Shipping-Address": "",
      "Shipping-City": "",
      "Shipping-State": "",
      "Shipping-zip": "",

      status: "Draft",
    });
  }, [leadId, lead, emptyEquipmentCounts]);

  const [form, setForm] = useState(() => buildInitialForm());

  // stepper labels
  const steps = useMemo(
    () => [
      { key: "user", label: "User Details" },
      { key: "biz", label: "Business Details" },
      { key: "pricing", label: "Pricing" },
      { key: "banking", label: "Banking" },
      { key: "equipment", label: "Equipment" },
    ],
    []
  );

  const feeStructureOptions = useMemo(
    () => [
      { value: "interchange", label: "Interchange Plus" },
      { value: "flatfee", label: "Flat Fee" },
      { value: "surcharging", label: "Surcharging" },
    ],
    []
  );

  const equipmentOptions = useMemo(
    () => [
      { value: "paxa920", label: "PAX A920" },
      { value: "dejavoo-qd4", label: "Dejavoo QD4" },
      { value: "clover-go", label: "Clover Go" },
      { value: "clover-flex", label: "Clover Flex" },
      { value: "clover-mini", label: "Clover Mini" },
      { value: "clover-mini-with-Cashdrawer", label: "Clover Mini with Cash Drawer" },
      { value: "clover-station", label: "Clover Station" },
      { value: "clover-station-duo", label: "Clover Station Duo" },
    ],
    []
  );

  function update(field) {
    return (e) => {
      const val = e?.target?.value ?? e;
      setForm((f) => ({ ...f, [field]: val }));
      // do not show errors while typing unless Save was attempted
      if (attemptedSave && fieldErrors[field]) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    };
  }

  function applyShippingSource(src, baseForm) {
    const next = { ...baseForm };

    if (src === "business") {
      next["Shipping-Address"] = next["Business-Address"] || "";
      next["Shipping-City"] = next["Business-City"] || "";
      next["Shipping-State"] = next["Business-State"] || "";
      next["Shipping-zip"] = next["Business-zip"] || "";
    } else if (src === "home") {
      next["Shipping-Address"] = next.street || "";
      next["Shipping-City"] = next.city || "";
      next["Shipping-State"] = next.state || "";
      next["Shipping-zip"] = next.postal_code || "";
    }
    // manual => do nothing
    return next;
  }

  // When modal opens or lead changes, FULL reset (prevents "last lead values" leak)
  useEffect(() => {
    if (!open) return;

    setBannerErr("");
    setBannerOk("");
    setStep(0);
    setCopyOwnerToBusiness(false);
    setSsnOptional(false);
    setShippingSource("manual");

    // reset validation state
    setAttemptedSave(false);
    setFieldErrors({});

    setForm(buildInitialForm());
  }, [open, leadId, lead, buildInitialForm]);

  // Fetch existing MPA by lead_id and prefill (edit mode)
  useEffect(() => {
    let ignore = false;

    async function run() {
      if (!open) return;
      if (!leadId) return;

      setLoadingExisting(true);
      try {
        const [mpaResp, leadResp] = await Promise.all([
          API.get(`/auth/mpa-by-lead/${leadId}`),
          API.get(`/auth/lead/${leadId}`),
        ]);
        const data = mpaResp?.data;
        if (!data || ignore) return;

        if (!ignore) {
          const ld = leadResp?.data || {};
          setLeadFlags({
            is_reprogram: Number(ld.is_reprogram) || 0,
            is_duplicate: Number(ld.is_duplicate) || 0,
            source_lead_id: ld.source_lead_id ?? null,
          });
        }

        setForm((f) => {
          const hydrated = {
            ...f,

            lead_id: pick(data, "lead_id", f.lead_id),
            name: pick(data, "name", f.name),
            bname: pick(data, "bname", f.bname),
            email: pick(data, "email", f.email),
            phone: pick(data, "phone", f.phone),
            agent: pick(data, "agent", f.agent),

            // owner address
            street: pick(data, "street", f.street),
            city: pick(data, "city", f.city),
            state: pick(data, "state", f.state),
            postal_code: pick(data, "postal_code", f.postal_code),
            DOB: pick(data, "DOB", f.DOB),
            SSN: pick(data, "SSN", f.SSN),

            // business
            "Legal-Name": hasOwn(data, "Legal-Name")
              ? pick(data, "Legal-Name", "")
              : hasOwn(data, "LegalName")
                ? pick(data, "LegalName", "")
                : f["Legal-Name"],

            "DBA-Name": hasOwn(data, "DBA-Name")
              ? pick(data, "DBA-Name", "")
              : hasOwn(data, "DBAName")
                ? pick(data, "DBAName", "")
                : f["DBA-Name"],

            "Tax-id": hasOwn(data, "Tax-id")
              ? pick(data, "Tax-id", "")
              : hasOwn(data, "TaxId")
                ? pick(data, "TaxId", "")
                : f["Tax-id"],

            Website: pick(data, "Website", f.Website),
            "ownership-type": pick(data, "ownership-type", f["ownership-type"]),
            "Business-Address": pick(data, "Business-Address", f["Business-Address"]),
            "Business-City": pick(data, "Business-City", f["Business-City"]),
            "Business-State": pick(data, "Business-State", f["Business-State"]),
            "Business-zip": pick(data, "Business-zip", f["Business-zip"]),
            "Business-Phone": pick(data, "Business-Phone", f["Business-Phone"]),
            "Business-start-date": pick(data, "Business-start-date", f["Business-start-date"]),
            "Avg-ticket": pick(data, "Avg-ticket", f["Avg-ticket"]),
            "High-ticket": pick(data, "High-ticket", f["High-ticket"]),
            "total-monthly-processing": pick(
              data,
              "total-monthly-processing",
              f["total-monthly-processing"]
            ),
            "products-sold": pick(data, "products-sold", f["products-sold"]),

            // pricing
            feestructure: pick(data, "feestructure", f.feestructure),
            Pricing: pick(data, "Pricing", f.Pricing),
            PricingText: pick(data, "PricingText", f.PricingText),
            "Pricing-variable": pick(data, "Pricing-variable", f["Pricing-variable"]),
            "monthly-fee": pickNum(data, "monthly-fee", f["monthly-fee"] ?? 0),
"monthly-minimum": pickNum(data, "monthly-minimum", f["monthly-minimum"] ?? 0),

            // banking
            "Bank-name": pick(data, "Bank-name", f["Bank-name"]),
            "Bank-acc": pick(data, "Bank-acc", f["Bank-acc"]),
            Routing: pick(data, "Routing", f.Routing),

            // equipment + shipping
            equipement: pick(data, "equipement", f.equipement),
            paxa920: pickNum(data, "paxa920", f.paxa920 ?? 0),
            "dejavoo-qd4": pickNum(data, "dejavoo-qd4", f["dejavoo-qd4"] ?? 0),
            "clover-go": pickNum(data, "clover-go", f["clover-go"] ?? 0),
            "clover-flex": pickNum(data, "clover-flex", f["clover-flex"] ?? 0),
            "clover-mini": pickNum(data, "clover-mini", f["clover-mini"] ?? 0),
            "clover-mini-with-Cashdrawer": pickNum(data, "clover-mini-with-Cashdrawer", f["clover-mini-with-Cashdrawer"] ?? 0),
            "clover-station": pickNum(data, "clover-station", f["clover-station"] ?? 0),
            "clover-station-duo": pickNum(
              data,
              "clover-station-duo",
              f["clover-station-duo"] ?? 0
            ),

            "Shipping-Address": pick(data, "Shipping-Address", f["Shipping-Address"]),
            "Shipping-City": pick(data, "Shipping-City", f["Shipping-City"]),
            "Shipping-State": pick(data, "Shipping-State", f["Shipping-State"]),
            "Shipping-zip": pick(data, "Shipping-zip", f["Shipping-zip"]),

            status: pick(data, "status", f.status || "Draft"),
          };

          return hydrated;
        });

        // Pre-select createdby in admin dropdown if editing existing record
        if (data.createdby) setSelectedCreatedBy(data.createdby);
      } catch (e) {
        if (e?.response?.status !== 404) console.error(e);
      } finally {
        if (!ignore) setLoadingExisting(false);
      }
    }

    run();
    return () => {
      ignore = true;
    };
  }, [open, leadId]);

  // Copy owner address -> business address
  useEffect(() => {
    if (!copyOwnerToBusiness) return;

    setForm((f) => ({
      ...f,
      "Business-Address": f.street || "",
      "Business-City": f.city || "",
      "Business-State": f.state || "",
      "Business-zip": f.postal_code || "",
    }));
  }, [copyOwnerToBusiness, form.street, form.city, form.state, form.postal_code]);

  // fee structure logic
  useEffect(() => {
    const fs = form.feestructure;
    setForm((f) => {
      if (fs === "surcharging") return { ...f, PricingText: "" };
      if (fs === "interchange" || fs === "flatfee") return { ...f, Pricing: "" };
      return f;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.feestructure]);

  // pricing dropdown change -> auto set Pricing-variable
  useEffect(() => {
    if (form.feestructure !== "surcharging") return;
    const p = String(form.Pricing || "");
    const fee = p === "3" ? "2.912" : p === "4" ? "3.8461" : "";
    setForm((f) => ({ ...f, "Pricing-variable": fee }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.Pricing, form.feestructure]);

  // equipment selection
  function onEquipmentSelect(val) {
    const chosen = String(val || "");
    setForm((f) => {
      const next = { ...f, equipement: chosen };
      equipmentKeys.forEach((k) => (next[k] = 0));
      if (chosen && equipmentKeys.includes(chosen)) next[chosen] = 1;
      return next;
    });
  }

  function extractApiError(err) {
    const res = err?.response;
    if (res?.data?.error) return res.data.error;
    if (res?.data?.message) return res.data.message;
    if (err?.message) return err.message;
    return "Request failed.";
  }

  // ========= Validation: only on final Save =========
  function validateAll(currentForm) {
    const f = currentForm;
    const errors = {};
    const stepOfField = {}; // used to jump to first step with errors

    function req(field, stepIndex, message) {
      stepOfField[field] = stepIndex;
      if (isBlank(f[field])) errors[field] = message || "Required";
    }

    // Step 1 required
    req("email", 0, "Email is required");
    req("phone", 0, "Phone is required");
    req("DOB", 0, "DOB is required");
    if (!isBlank(f.DOB) && !isValidDateValue(f.DOB)) {
      stepOfField.DOB = 0;
      errors.DOB = "Invalid date — use MM-DD-YYYY or YYYY-MM-DD";
    }

    if (!ssnOptional) {
      req("SSN", 0, "SSN is required (or mark optional)");
    }
    // SSN format check (only if present)
    if (!isValidSSNOrBlank(f.SSN)) {
      stepOfField.SSN = 0;
      errors.SSN = "SSN must be exactly 9 digits";
    }

    // Step 2 required
    req("Legal-Name", 1, "Legal Name is required");
    req("DBA-Name", 1, "DBA Name is required");
    req("ownership-type", 1, "Ownership Type is required");
    req("Business-Address", 1, "Business Address is required");
    req("Business-start-date", 1, "Business Start Date is required");
    if (!isBlank(f["Business-start-date"]) && !isValidDateValue(f["Business-start-date"])) {
      stepOfField["Business-start-date"] = 1;
      errors["Business-start-date"] = "Invalid date — use MM-DD-YYYY or YYYY-MM-DD";
    }

    // Step 3 required
    req("feestructure", 2, "Fee Structure is required");

    if (!isBlank(f.feestructure)) {
      if (f.feestructure === "surcharging") {
        req("Pricing", 2, "Pricing is required");
        req("Pricing-variable", 2, "Fee is required");
      } else if (f.feestructure === "interchange" || f.feestructure === "flatfee") {
        req("PricingText", 2, "Pricing text is required");
        req("Pricing-variable", 2, "Fee is required");
      }
    }

    // Step 4 required
    req("Bank-name", 3, "Bank name is required");
    req("Routing", 3, "Routing is required");

    // find first step that has any error
    let firstStep = 0;
    const errorFields = Object.keys(errors);
    if (errorFields.length) {
      firstStep = Math.min(
        ...errorFields.map((fld) => (typeof stepOfField[fld] === "number" ? stepOfField[fld] : 0))
      );
    }

    return { errors, firstStep };
  }

  function hasError(field) {
    // Show errors either after a save attempt OR from on-blur date validation
    return !!fieldErrors[field];
  }

  // ========= Navigation (free) =========
  function goToStep(i) {
    setBannerErr("");
    setBannerOk("");
    setStep(i);
  }

  function back() {
    setBannerErr("");
    setBannerOk("");
    setStep((s) => Math.max(0, s - 1));
  }

  async function saveAndContinue() {
    setBannerErr("");
    setBannerOk("");

    if (!leadId && isBlank(form.lead_id)) {
      setBannerErr("Missing lead_id.");
      return;
    }

    setSaving(true);
    try {
      const { username, role } = getUsernameAndRole();
      const resolvedCreatedBy = isAdmin && selectedCreatedBy ? selectedCreatedBy : (username || "Unknown");

      const payload = {
        ...form,
        lead_id: leadId || form.lead_id,
        createdby: resolvedCreatedBy,
        role: role || "",
        DOB: toMysqlDate(form.DOB),
        "Business-start-date": toMysqlDate(form["Business-start-date"]),
      };

      await API.post("/auth/mpa-upsert", payload);

      setBannerOk("Saved.");
      setTimeout(() => setBannerOk(""), 1500);
      setStep((s) => Math.min(steps.length - 1, s + 1));
    } catch (e) {
      console.error(e);
      setBannerErr(extractApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function save() {
    setBannerErr("");
    setBannerOk("");

    setAttemptedSave(true);

    // const { errors, firstStep } = validateAll(form);
    // setFieldErrors(errors);

    // if (Object.keys(errors).length) {
    //   setBannerErr("Please fix the highlighted required fields.");
    //   setStep(firstStep);
    //   return;
    // }

    if (!leadId && isBlank(form.lead_id)) {
      setBannerErr("Missing lead_id.");
      return;
    }

    setSaving(true);
    try {
      const { username, role } = getUsernameAndRole();
      const resolvedCreatedBy = isAdmin && selectedCreatedBy ? selectedCreatedBy : (username || "Unknown");

      const payload = {
        ...form,
        lead_id: leadId || form.lead_id,
        createdby: resolvedCreatedBy,
        role: role || "",

        DOB: toMysqlDate(form.DOB),
        "Business-start-date": toMysqlDate(form["Business-start-date"]),
      };

      const resp = await API.post("/auth/mpa-upsert", payload);

      setBannerOk(leadId ? "Document updated." : "Document created.");
      setTimeout(() => setBannerOk(""), 2000);

      onSaved?.(resp?.data || payload);
      onClose?.();
    } catch (e) {
      console.error(e);
      setBannerErr(extractApiError(e));
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  const showPricingDropdown = form.feestructure === "surcharging";
  const showPricingText = form.feestructure === "interchange" || form.feestructure === "flatfee";
  const showPricingVar = showPricingDropdown || showPricingText;

  const isEditMode = !!leadId;

  const RequiredStar = () => <span className="ldm-required">*</span>;

  return (
    <div className="ldm-backdrop" role="presentation">
      <div
        className="ldm-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <div className="ldm-header">
          <h3>
            {isEditMode ? "Edit Document" : "Create Document"}
            <br />
            <span style={{ fontSize: 14, fontWeight: 400, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 2 }}>
              {leadId ? `Lead ID: ${leadId}` : "New Document"}
              {(Number(leadFlags.is_duplicate) === 1 || Number(lead?.is_duplicate) === 1) && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: '#ede9fe',
                    color: '#5b21b6',
                    border: '1px solid #c4b5fd',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 10px',
                    letterSpacing: 0.3,
                  }}
                  title={`This document belongs to a duplicate lead (original Lead #${leadFlags.source_lead_id || lead?.source_lead_id})`}
                >
                  ⧉ DUPLICATE of #{leadFlags.source_lead_id || lead?.source_lead_id || '—'}
                </span>
              )}
              {(Number(leadFlags.is_reprogram) === 1 || Number(lead?.is_reprogram) === 1) && (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 4,
                    background: '#fef3c7',
                    color: '#92400e',
                    border: '1px solid #fcd34d',
                    borderRadius: 999,
                    fontSize: 11,
                    fontWeight: 700,
                    padding: '2px 10px',
                    letterSpacing: 0.3,
                  }}
                  title="This is a reprogram lead — equipment replacement request"
                >
                  ↺ REPROGRAM
                </span>
              )}
            </span>
          </h3>

          <div className="ldm-actions">
            {bannerErr && <span className="ldm-banner ldm-banner-error">{bannerErr}</span>}
            {bannerOk && <span className="ldm-banner ldm-banner-ok">{bannerOk}</span>}
            <button className="ldm-close" onClick={onClose} aria-label="Close">
              ✕
            </button>
          </div>
        </div>

        {loadingExisting && <div className="ldm-body">Loading…</div>}

        {!loadingExisting && (
          <div className="ldm-body">
            {/* Stepper (clickable) */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 18,
                marginBottom: 12,
                padding: "0 2px",
                flexWrap: "wrap",
              }}
            >
              {steps.map((s, i) => {
                const active = i === step;
                const done = i < step;
                return (
                  <div
                    key={s.key}
                    className="ldm-stepper-item"
                    onClick={() => goToStep(i)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") goToStep(i);
                    }}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      opacity: done ? 0.7 : 1,
                      cursor: "pointer",
                    }}
                    aria-current={active ? "step" : undefined}
                  >
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 999,
                        background: active || done ? "#3b82f6" : "#e5e7eb",
                        color: active || done ? "#fff" : "#374151",
                        display: "grid",
                        placeItems: "center",
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {i + 1}
                    </div>
                    <div style={{ fontWeight: 600, color: active ? "#111827" : "#6b7280" }}>
                      {s.label}
                    </div>
                    {i < steps.length - 1 && (
                      <div
                        style={{
                          width: 36,
                          height: 2,
                          background: i < step ? "#3b82f6" : "#e5e7eb",
                          borderRadius: 999,
                          margin: "0 8px",
                        }}
                      />
                    )}
                  </div>
                );
              })}
            </div>

            {/* STEP 1 */}
            {step === 0 && (
              <div className="ldm-grid" style={{ marginTop: 8 }}>
                {isAdmin && (
                  <div className="ldm-field" style={{ gridColumn: "1 / -1" }}>
                    <label style={{ fontWeight: 700 }}>Created By (Admin)</label>
                    <select
                      value={selectedCreatedBy}
                      onChange={(e) => setSelectedCreatedBy(e.target.value)}
                    >
                      <option value="">— select user —</option>
                      {allUsers.map((u) => (
                        <option key={u.id || u.username} value={u.username}>
                          {u.username} {u.role ? `(${u.role})` : ""}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="ldm-field">
                  <label>Full Name</label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Full Name"
                  />
                </div>

                <div className="ldm-field">
                  <label>
                    Email 
                  </label>
                  <input
                    className={hasError("email") ? "ldm-input-error" : ""}
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="Email"
                  />
                </div>

                <div className="ldm-field">
                  <label>
                    Phone 
                  </label>
                  <input
                    className={hasError("phone") ? "ldm-input-error" : ""}
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="Phone"
                  />
                </div>

                <div className="ldm-field" style={{ gridColumn: "1 / -1" }}>
                  <label>Owner Street Address</label>
                  <input value={form.street} onChange={update("street")} placeholder="Street" />
                </div>

                <div className="ldm-field">
                  <label>City</label>
                  <input value={form.city} onChange={update("city")} placeholder="City" />
                </div>

                <div className="ldm-field">
                  <label>State</label>
                  <input value={form.state} onChange={update("state")} placeholder="State" />
                </div>

                <div className="ldm-field">
                  <label>Postal Code</label>
                  <input value={form.postal_code} onChange={update("postal_code")} placeholder="Zip" />
                </div>

                <div className="ldm-field">
                  <label>
                    DOB (MM-DD-YYYY or YYYY-MM-DD)
                  </label>
                  <input
                    className={hasError("DOB") ? "ldm-input-error" : ""}
                    value={toDateInput(form.DOB)}
                    onChange={update("DOB")}
                    onBlur={() => {
                      const v = form.DOB;
                      if (v && !isValidDateValue(v)) {
                        setFieldErrors((prev) => ({ ...prev, DOB: "Invalid date — use MM-DD-YYYY or YYYY-MM-DD" }));
                      } else {
                        setFieldErrors((prev) => { const next = { ...prev }; delete next.DOB; return next; });
                      }
                    }}
                    placeholder="MM-DD-YYYY"
                  />
                  {hasError("DOB") && (
                    <span style={{ color: "#dc2626", fontSize: 12, marginTop: 3, display: "block" }}>
                      {fieldErrors["DOB"]}
                    </span>
                  )}
                </div>

                <div className="ldm-field">
                  <label>
                    SSN (9 digits) {!ssnOptional && <RequiredStar />}
                  </label>
                  <input
                    className={hasError("SSN") ? "ldm-input-error" : ""}
                    value={form.SSN}
                    onChange={update("SSN")}
                    placeholder="#########"
                    maxLength={9}
                    disabled={ssnOptional}
                  />
                  <div style={{ marginTop: 6 }}>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 600 }}>
                      <input
                        type="checkbox"
                        checked={ssnOptional}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setSsnOptional(checked);
                          if (checked) {
                            setForm((f) => ({ ...f, SSN: "" }));
                            // clear SSN errors if any
                            setFieldErrors((prev) => {
                              const next = { ...prev };
                              delete next.SSN;
                              return next;
                            });
                          }
                        }}
                      />
                      Optional SSN
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* STEP 2 */}
            {step === 1 && (
              <div className="ldm-grid" style={{ marginTop: 8 }}>
                <div className="ldm-field">
                  <label>
                    Legal Name 
                  </label>
                  <input
                    className={hasError("Legal-Name") ? "ldm-input-error" : ""}
                    value={form["Legal-Name"]}
                    onChange={update("Legal-Name")}
                    placeholder="Legal Name"
                  />
                </div>

                <div className="ldm-field">
                  <label>
                    DBA Name 
                  </label>
                  <input
                    className={hasError("DBA-Name") ? "ldm-input-error" : ""}
                    value={form["DBA-Name"]}
                    onChange={update("DBA-Name")}
                    placeholder="DBA Name"
                  />
                </div>

                <div className="ldm-field">
                  <label>Tax ID</label>
                  <input value={form["Tax-id"]} onChange={update("Tax-id")} placeholder="Tax ID" />
                </div>

                <div className="ldm-field">
                  <label>Website</label>
                  <input value={form.Website} onChange={update("Website")} placeholder="Website" />
                </div>

                <div className="ldm-field">
                  <label>
                    Ownership Type 
                  </label>
                  <select
                    className={hasError("ownership-type") ? "ldm-input-error" : ""}
                    value={form["ownership-type"]}
                    onChange={update("ownership-type")}
                  >
                    <option value="" disabled>
                      Select Ownership Type
                    </option>
                    <option value="Sole-Proprietorship">Sole Proprietorship</option>
                    <option value="Partnership">Partnership</option>
                    <option value="Corporation">Corporation</option>
                    <option value="LLC">LLC</option>
                    <option value="Non-Profit">Non-Profit</option>
                    <option value="Government">Government</option>
                  </select>
                </div>

                <div className="ldm-field" style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700 }}>
                    <input
                      type="checkbox"
                      checked={copyOwnerToBusiness}
                      onChange={(e) => setCopyOwnerToBusiness(e.target.checked)}
                    />
                    Use same address as owner
                  </label>
                </div>

                <div className="ldm-field" style={{ gridColumn: "1 / -1" }}>
                  <label>
                    Business Address 
                  </label>
                  <input
                    className={hasError("Business-Address") ? "ldm-input-error" : ""}
                    value={form["Business-Address"]}
                    onChange={update("Business-Address")}
                    placeholder="Business Street"
                    readOnly={copyOwnerToBusiness}
                    aria-readonly={copyOwnerToBusiness ? "true" : "false"}
                  />
                </div>

                <div className="ldm-field">
                  <label>Business City</label>
                  <input
                    value={form["Business-City"]}
                    onChange={update("Business-City")}
                    placeholder="City"
                    readOnly={copyOwnerToBusiness}
                    aria-readonly={copyOwnerToBusiness ? "true" : "false"}
                  />
                </div>

                <div className="ldm-field">
                  <label>Business State</label>
                  <input
                    value={form["Business-State"]}
                    onChange={update("Business-State")}
                    placeholder="State"
                    readOnly={copyOwnerToBusiness}
                    aria-readonly={copyOwnerToBusiness ? "true" : "false"}
                  />
                </div>

                <div className="ldm-field">
                  <label>Business Zip</label>
                  <input
                    value={form["Business-zip"]}
                    onChange={update("Business-zip")}
                    placeholder="Zip"
                    readOnly={copyOwnerToBusiness}
                    aria-readonly={copyOwnerToBusiness ? "true" : "false"}
                  />
                </div>

                <div className="ldm-field">
                  <label>Business Phone</label>
                  <input
                    value={form["Business-Phone"]}
                    onChange={update("Business-Phone")}
                    placeholder="Business Phone"
                  />
                </div>

                <div className="ldm-field">
                  <label>
                    Business Start Date (MM-DD-YYYY or YYYY-MM-DD)
                  </label>
                  <input
                    className={hasError("Business-start-date") ? "ldm-input-error" : ""}
                    value={toDateInput(form["Business-start-date"])}
                    onChange={update("Business-start-date")}
                    onBlur={() => {
                      const v = form["Business-start-date"];
                      if (v && !isValidDateValue(v)) {
                        setFieldErrors((prev) => ({ ...prev, "Business-start-date": "Invalid date — use MM-DD-YYYY or YYYY-MM-DD" }));
                      } else {
                        setFieldErrors((prev) => { const next = { ...prev }; delete next["Business-start-date"]; return next; });
                      }
                    }}
                    placeholder="MM-DD-YYYY"
                  />
                  {hasError("Business-start-date") && (
                    <span style={{ color: "#dc2626", fontSize: 12, marginTop: 3, display: "block" }}>
                      {fieldErrors["Business-start-date"]}
                    </span>
                  )}
                </div>

                <div className="ldm-field">
                  <label>Average Ticket</label>
                  <input value={form["Avg-ticket"]} onChange={update("Avg-ticket")} placeholder="Avg Ticket" />
                </div>

                <div className="ldm-field">
                  <label>High Ticket</label>
                  <input value={form["High-ticket"]} onChange={update("High-ticket")} placeholder="High Ticket" />
                </div>

                <div className="ldm-field">
                  <label>Total Monthly Processing</label>
                  <input
                    value={form["total-monthly-processing"]}
                    onChange={update("total-monthly-processing")}
                    placeholder="Total monthly processing"
                  />
                </div>

                <div className="ldm-field ldm-span-2">
                  <label>Description of Products Sold</label>
                  <input
                    value={form["products-sold"]}
                    onChange={update("products-sold")}
                    placeholder="Description of products sold"
                  />
                </div>
              </div>
            )}

            {/* STEP 3 */}
            {step === 2 && (
              <div className="ldm-grid" style={{ marginTop: 8 }}>
                <div className="ldm-field">
                  <label>
                    Fee Structure 
                  </label>
                  <select
                    className={hasError("feestructure") ? "ldm-input-error" : ""}
                    value={form.feestructure}
                    onChange={update("feestructure")}
                  >
                    <option value="" disabled>
                      Select Fee Structure
                    </option>
                    {feeStructureOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ldm-field">
  <label>Monthly Fee</label>
  <input
    type="number"
    inputMode="decimal"
    min="0"
    step="0.01"
    value={form["monthly-fee"]}
    onChange={update("monthly-fee")}
    placeholder="Monthly fee"
  />
</div>

<div className="ldm-field">
  <label>Monthly Minimum</label>
  <input
    type="number"
    inputMode="decimal"
    min="0"
    step="0.01"
    value={form["monthly-minimum"]}
    onChange={update("monthly-minimum")}
    placeholder="Monthly minimum"
  />
</div>


                {showPricingDropdown && (
                  <div className="ldm-field">
                    <label>
                      Pricing 
                    </label>
                    <select
                      className={hasError("Pricing") ? "ldm-input-error" : ""}
                      value={form.Pricing}
                      onChange={update("Pricing")}
                    >
                      <option value="" disabled>
                        Select Pricing
                      </option>
                      <option value="3">3</option>
                      <option value="4">4</option>
                    </select>
                  </div>
                )}

                {showPricingText && (
                  <div className="ldm-field" style={{ gridColumn: "1 / -1" }}>
                    <label>
                      Pricing 
                    </label>
                    <input
                      className={hasError("PricingText") ? "ldm-input-error" : ""}
                      value={form.PricingText}
                      onChange={update("PricingText")}
                      placeholder="Pricing text"
                    />
                  </div>
                )}

                {showPricingVar && (
                  <div className="ldm-field">
                    <label>
                      Fee 
                    </label>
                    <input
                      className={hasError("Pricing-variable") ? "ldm-input-error" : ""}
                      value={form["Pricing-variable"]}
                      onChange={update("Pricing-variable")}
                      placeholder="Pricing fee"
                    />
                  </div>
                )}
              </div>
            )}

            {/* STEP 4 */}
            {step === 3 && (
              <div className="ldm-grid" style={{ marginTop: 8 }}>
                <div className="ldm-field">
                  <label>
                    Bank Name 
                  </label>
                  <input
                    className={hasError("Bank-name") ? "ldm-input-error" : ""}
                    value={form["Bank-name"]}
                    onChange={update("Bank-name")}
                    placeholder="Bank name"
                  />
                </div>

                <div className="ldm-field">
                  <label>Bank Account</label>
                  <input value={form["Bank-acc"]} onChange={update("Bank-acc")} placeholder="Account number" />
                </div>

                <div className="ldm-field">
                  <label>
                    Routing Number 
                  </label>
                  <input
                    className={hasError("Routing") ? "ldm-input-error" : ""}
                    value={form.Routing}
                    onChange={update("Routing")}
                    placeholder="Routing"
                  />
                </div>
              </div>
            )}

            {/* STEP 5 */}
            {step === 4 && (
              <div className="ldm-grid" style={{ marginTop: 8 }}>
                <div className="ldm-field">
                  <label>Equipment</label>
                  <select value={form.equipement} onChange={(e) => onEquipmentSelect(e.target.value)}>
                    <option value="" disabled>
                      Select Equipment
                    </option>
                    {equipmentOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="ldm-field">
                  <label>Status</label>
                  <select value={form.status} onChange={update("status")} hidden={true}>
                    <option value="signed">signed</option>
                    <option value="approved">approved</option>
                    <option value="HF-Filled">HF-Filled</option>
                    <option value="submitted">submitted</option>
                    <option value="Pending Approval">Pending Approval</option>
                    <option value="Declined">Declined</option>
                  </select>
                </div>


                {/* show the generated counts */}
                {/* <div className="ldm-field">
                  <label>PAX A920</label> */}
                  <input value={form.paxa920} readOnly aria-readonly="true" hidden />
                {/* </div> */}

                {/* <div className="ldm-field">
                  <label>Dejavoo QD4</label> */}
                  <input value={form["dejavoo-qd4"]} readOnly aria-readonly="true"  hidden/>
                {/* </div> */}

                {/* <div className="ldm-field">
                  <label>Clover Go</label> */}
                  <input value={form["clover-go"]} readOnly aria-readonly="true"  hidden />
                {/* </div> */}

                {/* <div className="ldm-field">
                  <label>Clover Flex</label> */}
                  <input value={form["clover-flex"]} readOnly aria-readonly="true" hidden />
                {/* </div> */}

                {/* <div className="ldm-field">
                  <label>Clover Mini</label> */}
                  <input value={form["clover-mini"]} readOnly aria-readonly="true" hidden />
                {/* </div> */}

                 {/* <div className="ldm-field">
                  <label>Clover Mini with Cash Drawer</label> */}
                  <input value={form["clover-mini-with-Cashdrawer"]} readOnly aria-readonly="true" hidden />
                {/* </div> */}

                {/* <div className="ldm-field">
                  <label>Clover Station</label> */}
                  <input value={form["clover-station"]} readOnly aria-readonly="true" hidden/>
                {/* </div> */}

                {/* <div className="ldm-field">
                  <label>Clover Station Duo</label> */}
                  <input value={form["clover-station-duo"]} readOnly aria-readonly="true" hidden />
                {/* </div> */}

                {/* shipping source selector */}
                <div className="ldm-field" style={{ gridColumn: "1 / -1" }}>
                  <label>Shipping Address Source</label>
                  <select
                    value={shippingSource}
                    onChange={(e) => {
                      const src = e.target.value;
                      setShippingSource(src);
                      setForm((f) => applyShippingSource(src, f));
                    }}
                  >
                    <option value="manual">Manual (enter below)</option>
                    <option value="business">Use Business Address</option>
                    <option value="home">Use Home Address</option>
                  </select>
                </div>

                <div className="ldm-field" style={{ gridColumn: "1 / -1" }}>
                  <label>Shipping Address</label>
                  <input
                    value={form["Shipping-Address"]}
                    onChange={update("Shipping-Address")}
                    placeholder="Shipping address"
                  />
                </div>

                <div className="ldm-field">
                  <label>Shipping City</label>
                  <input value={form["Shipping-City"]} onChange={update("Shipping-City")} placeholder="City" />
                </div>

                <div className="ldm-field">
                  <label>Shipping State</label>
                  <input value={form["Shipping-State"]} onChange={update("Shipping-State")} placeholder="State" />
                </div>

                <div className="ldm-field">
                  <label>Shipping Zip</label>
                  <input value={form["Shipping-zip"]} onChange={update("Shipping-zip")} placeholder="Zip" />
                </div>
              </div>
            )}

            {/* footer */}
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
              {step > 0 && (
                <button className="ldm-btn" onClick={back} disabled={saving}>
                  Back
                </button>
              )}

              {step < steps.length - 1 && (
                <button className="ldm-btn ldm-btn-primary" onClick={saveAndContinue} disabled={saving}>
                  {saving ? "Saving…" : "Save & Continue"}
                </button>
              )}

              {step === steps.length - 1 && (
                <button className="ldm-btn ldm-btn-primary" onClick={save} disabled={saving}>
                  {saving ? "Saving…" : isEditMode ? "Update Document" : "Create Document"}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
